import type { SizeTier } from '@/utils/constants';
import { SIZE_TIERS } from '@/utils/constants';

export interface MassSystemOptions {
  initialMass?: number;
  tierThresholds?: readonly number[];
}

export type MassListener = (tier: SizeTier, mass: number) => void;

const DEFAULT_THRESHOLDS: readonly number[] = [0, 1, 5, 25, 100, 400];

export class MassSystem {
  private mass: number;
  private readonly thresholds: readonly number[];
  private listeners: MassListener[] = [];

  constructor(options: MassSystemOptions = {}) {
    this.mass = options.initialMass ?? 0;
    this.thresholds = options.tierThresholds ?? DEFAULT_THRESHOLDS;
  }

  add(amount: number): void {
    if (amount <= 0) return;
    const previousTier = this.currentTier;
    this.mass += amount;
    const newTier = this.currentTier;
    if (newTier !== previousTier) {
      this.emit(newTier);
    }
  }

  subtract(amount: number): void {
    if (amount <= 0) return;
    const previousTier = this.currentTier;
    this.mass = Math.max(0, this.mass - amount);
    const newTier = this.currentTier;
    if (newTier !== previousTier) {
      this.emit(newTier);
    }
  }

  /** Direct state replace — used by save-restore. Emits tier-change if the tier differs. */
  setMass(value: number): void {
    const next = Math.max(0, value);
    if (next === this.mass) return;
    const previousTier = this.currentTier;
    this.mass = next;
    const newTier = this.currentTier;
    if (newTier !== previousTier) {
      this.emit(newTier);
    }
  }

  get currentMass(): number {
    return this.mass;
  }

  get currentTier(): SizeTier {
    for (let i = this.thresholds.length - 1; i >= 0; i--) {
      if (this.mass >= this.thresholds[i]) {
        return (i + 1) as SizeTier;
      }
    }
    return SIZE_TIERS.PEA;
  }

  canEat(targetMass: number): boolean {
    return this.mass > targetMass;
  }

  speedMultiplier(): number {
    const tier = this.currentTier;
    return 1 / (1 + (tier - 1) * 0.15);
  }

  onTierChange(listener: MassListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private emit(tier: SizeTier): void {
    for (const listener of this.listeners) {
      listener(tier, this.mass);
    }
  }
}
